import React, { useState, useCallback } from 'react';
import { CheckCircle, XCircle, AlertTriangle, Info, X } from 'lucide-react';
import { cn } from '../../lib/utils';
import { NotificationContext, NotificationOptions } from '../../hooks/useNotification';

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isMounted, setIsMounted] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [config, setConfig] = useState<NotificationOptions>({ message: '', type: 'info' });

  const closeNotification = useCallback(() => {
    setIsVisible(false);
    setTimeout(() => {
      setIsMounted(false);
      if (config.onClose) config.onClose();
    }, 300);
  }, [config]);

  const showNotification = useCallback(
    (options: NotificationOptions) => {
      setConfig(options);
      setIsMounted(true);
      // Small delay to ensure render happens before transition
      requestAnimationFrame(() => {
        setIsVisible(true);
      });

      if (options.duration && options.duration > 0) {
        setTimeout(() => {
          closeNotification();
        }, options.duration);
      }
    },
    [closeNotification],
  );

  return (
    <NotificationContext.Provider value={{ showNotification, closeNotification }}>
      {children}
      {isMounted && (
        <NotificationPopup config={config} onClose={closeNotification} isVisible={isVisible} />
      )}
    </NotificationContext.Provider>
  );
};

const NotificationPopup: React.FC<{
  config: NotificationOptions;
  onClose: () => void;
  isVisible: boolean;
}> = ({ config, onClose, isVisible }) => {
  const { type = 'info', title, message } = config;

  const icons = {
    success: <CheckCircle className="w-12 h-12 text-emerald-500" />,
    error: <XCircle className="w-12 h-12 text-red-500" />,
    warning: <AlertTriangle className="w-12 h-12 text-amber-500" />,
    info: <Info className="w-12 h-12 text-blue-500" />,
  };

  const bgColors = {
    success: 'bg-emerald-50',
    error: 'bg-red-50',
    warning: 'bg-amber-50',
    info: 'bg-blue-50',
  };

  const buttonColors = {
    success: 'bg-emerald-600 hover:bg-emerald-700 focus:ring-emerald-200',
    error: 'bg-red-600 hover:bg-red-700 focus:ring-red-200',
    warning: 'bg-amber-600 hover:bg-amber-700 focus:ring-amber-200',
    info: 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-200',
  };

  return (
    <div
      className={cn(
        'fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm transition-opacity duration-300 ease-in-out',
        isVisible ? 'opacity-100' : 'opacity-0',
      )}
    >
      <div
        className={cn(
          'relative w-full max-w-sm bg-white rounded-2xl shadow-2xl p-6 transition-all duration-300 ease-out transform',
          isVisible ? 'scale-100 translate-y-0 opacity-100' : 'scale-95 translate-y-4 opacity-0',
        )}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex flex-col items-center text-center space-y-4">
          <div className={cn('p-3 rounded-full', bgColors[type])}>{icons[type]}</div>

          <div className="space-y-2">
            <h3 className="text-xl font-bold text-slate-900">
              {title || type.charAt(0).toUpperCase() + type.slice(1)}
            </h3>
            <p className="text-slate-600 text-sm leading-relaxed">{message}</p>
          </div>

          <button
            onClick={onClose}
            className={cn(
              'w-full py-2.5 px-4 rounded-lg text-white font-medium transition-all transform active:scale-95 focus:outline-none focus:ring-4',
              buttonColors[type],
            )}
          >
            OK, Mengerti
          </button>
        </div>
      </div>
    </div>
  );
};
